import { Pmp_Configs, TokenDetailsFragment } from '@/gql/generated/graphql'
import { AttributeField, AttributeFilter } from '@utils/token-features'
import { TokenInfoListItem } from './token-info-list-item'
import { createSerializer } from 'nuqs'
import { tokenFilterParsers } from '@/app/collection/[idOrSlug]/components/project-tokens/utils'
import Link from 'next/link'
import { projectShowRoute } from '@utils/routes'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import {
  BodyText,
  DetailText,
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipTrigger
} from 'design-configuration/components'
import { PmpCustomizableDetails } from './pmp-customizable-details'

export enum AttributeTag {
  Customizable = 'Customizable'
}

export function Attributes({ token }: { token: TokenDetailsFragment }) {
  return (
    <div className="p-sm gap-xs flex h-full flex-col">
      {Object.entries(token.features).length ? (
        Object.entries(token.features).map(([label, feature]) => (
          <AttributesItem key={label} label={label} feature={feature as string | number | boolean} token={token} />
        ))
      ) : (
        <div className="gap-xs text-on-surface-variant flex h-full w-full flex-col items-center justify-center">
          <BodyText className="font-medium">No attributes to display</BodyText>
          <BodyText>This artwork does not have any defined attributes.</BodyText>
        </div>
      )}
    </div>
  )
}

function AttributesItem({
  label,
  feature,
  token
}: {
  label: string
  feature: string | number | boolean
  token: TokenDetailsFragment
}) {
  const featureString = `${feature}`
  const featureFieldCounts = token.project.features?.feature_fields_counts?.[label]
  const featureCount = featureFieldCounts ? featureFieldCounts[featureString] : 0
  const percentage = (featureCount / (token.project.invocations ?? 0)) * 100

  const pmpConfigKeys = token.project?.pmp_project_configs?.[0]?.pmp_config_keys
  // @dev okay to assume there is only one pmp config per project
  const pmpConfigs = token.project?.pmp_project_configs?.[0]?.pmp_configs
  const attributeTag = pmpConfigKeys?.includes?.(label) ? AttributeTag.Customizable : null
  const pmpConfig = pmpConfigs?.find((config) => config.key === label)

  const attributeFilter = constructAttributeFilter(label, featureString, token.project.features?.feature_fields ?? [])
  const serializer = createSerializer(tokenFilterParsers)
  const href = attributeFilter
    ? `${serializer(projectShowRoute(token.project.slug), { attributes: { [label]: attributeFilter } })}#artworks`
    : null

  const topRight = href ? (
    <Link href={href} className="z-10">
      <span className="text-on-surface-variant mb-xs text-xs font-medium uppercase">In Collection</span>
      <VisuallyHidden>View other tokens with {featureString}</VisuallyHidden>
    </Link>
  ) : (
    'In Collection'
  )

  const bottomRight = href ? (
    <Link href={href} className="z-10">
      <span className="text-on-surface-variant text-sm">{`${featureCount} (${percentage.toFixed(2)}%)`}</span>
      <VisuallyHidden>View other tokens with {featureString}</VisuallyHidden>
    </Link>
  ) : (
    `${featureCount} (${percentage.toFixed(2)}%)`
  )

  const attributeTagWithTooltip = pmpConfig ? (
    <div className="gap-x-xs flex items-center">
      <DetailText className="text-on-surface-variant font-medium uppercase">{label}</DetailText>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="border-outline-variant text-on-surface-variant px-xs cursor-pointer rounded-full border">
            <BodyText className="text-xs leading-[130%]">{attributeTag}</BodyText>
          </div>
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent className="bg-surface p-md border-0 shadow-[4px_4px_8px_-2px_rgba(0,0,0,0.1)]">
            <PmpCustomizableDetails pmpDetails={pmpConfig as Pmp_Configs} colorTheme="primary" />
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    </div>
  ) : null

  return (
    <div className="rounded-xs hover:border-outline relative border border-transparent transition-colors duration-200">
      <TokenInfoListItem
        topLeft={label}
        bottomLeft={featureString}
        topRight={topRight}
        bottomRight={bottomRight}
        labelWithTooltip={attributeTagWithTooltip}
      />
    </div>
  )
}

function constructAttributeFilter(
  label: string,
  feature: string,
  featureFields: AttributeField[]
): AttributeFilter | null {
  const featureField = featureFields.find((field) => field.name === label)
  if (!featureField) {
    return null
  }

  if (featureField.type === 'enum' || featureField.type === 'boolean') {
    return {
      type: featureField.type,
      options: { [feature]: true }
    }
  }

  if (featureField.type === 'number') {
    return {
      type: 'number',
      min: Number(feature),
      max: Number(feature),
      domain: [featureField.min, featureField.max]
    }
  }

  return null
}
